import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FeedImportWizard } from '../FeedImportWizard'
import { useAccount } from '../../../hooks/useAccount'
import { api } from '../../../services/api'

const formatDate = (dateString, locale = 'de-DE') => {
  if (!dateString) return '-'
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(dateString))
}

const FEED_TYPE_LABELS = {
  google_shopping: 'Google Shopping',
  facebook_catalog: 'Facebook Catalog',
  custom: 'Custom XML'
}

function FeedManager() {
  const { t, i18n } = useTranslation('ecommerce')
  const { selectedAccount } = useAccount()
  const locale = i18n.language || 'de-DE'

  const [feeds, setFeeds] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showWizard, setShowWizard] = useState(false)
  const [syncingId, setSyncingId] = useState(null)
  const [syncingReviewsId, setSyncingReviewsId] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [reviewFeedModal, setReviewFeedModal] = useState(null) // { feedId, feedName, currentUrl }
  const [reviewFeedUrl, setReviewFeedUrl] = useState('')
  const [reviewFeedError, setReviewFeedError] = useState(null)
  const [savingReviewFeed, setSavingReviewFeed] = useState(false)

  const fetchFeeds = useCallback(async () => {
    if (!selectedAccount?.id) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await api.getFeeds()
      setFeeds(data.feeds || [])
    } catch (err) {
      setError(err.message || t('feeds.errors.load', 'Failed to load feeds'))
    } finally {
      setLoading(false)
    }
  }, [selectedAccount, t])

  useEffect(() => {
    fetchFeeds()
  }, [fetchFeeds])

  const handleSync = useCallback(async (feedId) => {
    setSyncingId(feedId)
    try {
      await api.syncFeed(feedId)
      await fetchFeeds()
    } catch (err) {
      setError(err.message || t('feeds.errors.sync', 'Failed to sync feed'))
    } finally {
      setSyncingId(null)
    }
  }, [fetchFeeds, t])

  const handleDelete = useCallback(async (feedId) => {
    try {
      await api.deleteFeed(feedId)
      setDeleteConfirm(null)
      await fetchFeeds()
    } catch (err) {
      setError(err.message || t('feeds.errors.delete', 'Failed to delete feed'))
    }
  }, [fetchFeeds, t])

  const handleWizardComplete = useCallback(async () => {
    setShowWizard(false)
    await fetchFeeds()
  }, [fetchFeeds])

  const openReviewFeedModal = useCallback((feed) => {
    setReviewFeedModal({
      feedId: feed.feed_id,
      feedName: feed.name,
      currentUrl: feed.review_feed_url || ''
    })
    setReviewFeedUrl(feed.review_feed_url || '')
    setReviewFeedError(null)
  }, [])

  const handleSaveReviewFeed = useCallback(async () => {
    if (!reviewFeedModal) return

    // Validate URL if provided
    if (reviewFeedUrl) {
      try {
        new URL(reviewFeedUrl)
      } catch {
        setReviewFeedError(t('feeds.reviewFeed.errors.invalidUrl', 'Please enter a valid URL'))
        return
      }
    }

    setSavingReviewFeed(true)
    setReviewFeedError(null)

    try {
      // Update the feed with the review feed URL
      await api.updateFeed(reviewFeedModal.feedId, {
        review_feed_url: reviewFeedUrl || null
      })

      // If URL was provided, trigger sync
      if (reviewFeedUrl) {
        setSyncingReviewsId(reviewFeedModal.feedId)
        try {
          const result = await api.syncReviews(reviewFeedModal.feedId)
          if (result.success) {
            // Show success - could add toast notification here
            // Reviews synced successfully
          }
        } catch (syncErr) {
          console.error('Review sync failed:', syncErr)
          // Don't fail - the URL was saved
        } finally {
          setSyncingReviewsId(null)
        }
      }

      setReviewFeedModal(null)
      await fetchFeeds()
    } catch (err) {
      setReviewFeedError(err.message || t('feeds.reviewFeed.errors.save', 'Failed to save review feed'))
    } finally {
      setSavingReviewFeed(false)
    }
  }, [reviewFeedModal, reviewFeedUrl, fetchFeeds, t])

  const handleSyncReviews = useCallback(async (feedId) => {
    setSyncingReviewsId(feedId)
    try {
      await api.syncReviews(feedId)
      await fetchFeeds()
    } catch (err) {
      setError(err.message || t('feeds.errors.syncReviews', 'Failed to sync reviews'))
    } finally {
      setSyncingReviewsId(null)
    }
  }, [fetchFeeds, t])

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12" role="status">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="sr-only">{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('feeds.title', 'Product Feeds')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('feeds.subtitle', 'Manage your product catalog connections')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowWizard(true)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('feeds.addFeed', 'Add Feed')}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {feeds.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {t('feeds.empty.title', 'No feeds configured')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('feeds.empty.message', 'Import your first product feed to get started.')}
          </p>
          <p className="mt-2 text-xs text-gray-400">
            {t('feeds.empty.hint', 'A product feed is a URL that contains your shop\'s product data. You\'ll find it in your shop system under Google Shopping or Facebook Shop.')}
          </p>
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
          >
            {t('feeds.addFirstFeed', 'Add your first feed')}
          </button>
        </div>
      ) : (
        /* Feed Table */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.name', 'Feed Name')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.type', 'Type')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.products', 'Products')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.lastSync', 'Last Sync')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.status', 'Status')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.reviews', 'Reviews')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feeds.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {feeds.map((feed) => (
                <React.Fragment key={feed.feed_id}>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{feed.name}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">{feed.feed_url}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {FEED_TYPE_LABELS[feed.feed_type] || feed.feed_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {feed.product_count} {t('feeds.products', 'products')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500">
                      {formatDate(feed.last_sync_at, locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {feed.last_sync_status === 'success' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('feeds.status.success', 'Success')}
                      </span>
                    ) : feed.last_sync_status === 'error' ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={feed.last_sync_error}>
                        {t('feeds.status.error', 'Error')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t('feeds.status.pending', 'Pending')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {feed.review_feed_url ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          {t('feeds.reviews.connected', 'Connected')}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleSyncReviews(feed.feed_id)}
                          disabled={syncingReviewsId === feed.feed_id}
                          className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                          title={t('feeds.actions.syncReviews', 'Sync Reviews')}
                        >
                          {syncingReviewsId === feed.feed_id ? (
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          )}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openReviewFeedModal(feed)}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 border border-purple-200"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('feeds.actions.addReviews', 'Add Reviews')}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      type="button"
                      onClick={() => handleSync(feed.feed_id)}
                      disabled={syncingId === feed.feed_id}
                      className="text-primary-600 hover:text-primary-900 mr-4 disabled:opacity-50"
                      aria-label={t('feeds.actions.sync', 'Sync feed')}
                    >
                      {syncingId === feed.feed_id ? (
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        t('feeds.actions.sync', 'Sync')
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(feed.feed_id)}
                      className="text-red-600 hover:text-red-900"
                      aria-label={t('feeds.actions.delete', 'Delete feed')}
                    >
                      {t('feeds.actions.delete', 'Delete')}
                    </button>
                  </td>
                </tr>
                {/* Review Feed Sub-Row */}
                {feed.review_feed_url && (
                  <tr className="bg-purple-50">
                    <td colSpan="7" className="px-6 py-2 pl-12">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span className="text-sm text-purple-700 font-medium">
                            {t('feeds.reviewFeed.subrow.title', 'Review Feed')}
                          </span>
                          <span className="text-xs text-purple-600 truncate max-w-md">
                            {feed.review_feed_url}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          {feed.review_stats ? (
                            <span className="text-sm text-purple-700">
                              <span className="font-semibold">{feed.review_stats.products_with_reviews}</span>
                              {' '}{t('feeds.reviewFeed.subrow.productsWithReviews', 'products with reviews')}
                              {' '}
                              <span className="text-purple-500">
                                ({feed.review_stats.total_reviews} {t('feeds.reviewFeed.subrow.totalReviews', 'reviews total')})
                              </span>
                            </span>
                          ) : (
                            <span className="text-sm text-purple-500 italic">
                              {t('feeds.reviewFeed.subrow.notSynced', 'Not yet synced')}
                            </span>
                          )}
                          {feed.review_feed_last_sync_at && (
                            <span className="text-xs text-purple-400">
                              {t('feeds.reviewFeed.subrow.lastSync', 'Last sync:')} {formatDate(feed.review_feed_last_sync_at, locale)}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => openReviewFeedModal(feed)}
                            className="text-purple-600 hover:text-purple-800"
                            title={t('feeds.actions.editReviewFeed', 'Edit Review Feed')}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import Wizard Modal */}
      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <FeedImportWizard
            onComplete={handleWizardComplete}
            onCancel={() => setShowWizard(false)}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900">
              {t('feeds.delete.title', 'Delete Feed')}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {t('feeds.delete.message', 'Are you sure you want to delete this feed? This action cannot be undone.')}
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Feed Modal */}
      {reviewFeedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('feeds.reviewFeed.title', 'Google Product Reviews')}
                </h3>
                <p className="text-sm text-gray-500">
                  {reviewFeedModal.feedName}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                {t('feeds.reviewFeed.description', 'Connect a Google Product Reviews feed to show real customer reviews in your product videos. Reviews are matched via GTIN, MPN, or SKU.')}
              </p>
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-3 text-sm text-purple-700">
                <strong>{t('feeds.reviewFeed.note', 'Note:')}</strong>{' '}
                {t('feeds.reviewFeed.limit', 'We import up to 100 of the best reviews that match your products.')}
              </div>
            </div>

            {reviewFeedError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-700">{reviewFeedError}</p>
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="review-feed-url-input" className="block text-sm font-medium text-gray-700 mb-1">
                {t('feeds.reviewFeed.urlLabel', 'Review Feed URL')}
              </label>
              <input
                id="review-feed-url-input"
                type="url"
                value={reviewFeedUrl}
                onChange={(e) => setReviewFeedUrl(e.target.value)}
                disabled={savingReviewFeed}
                placeholder="https://example.com/reviews.xml"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('feeds.reviewFeed.urlHint', 'Google Product Reviews XML feed (Schema v2.3 or v2.4)')}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setReviewFeedModal(null)}
                disabled={savingReviewFeed}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              {reviewFeedModal.currentUrl && (
                <button
                  type="button"
                  onClick={() => {
                    setReviewFeedUrl('')
                    handleSaveReviewFeed()
                  }}
                  disabled={savingReviewFeed}
                  className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                >
                  {t('feeds.reviewFeed.remove', 'Remove')}
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveReviewFeed}
                disabled={savingReviewFeed || !reviewFeedUrl}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingReviewFeed ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('common.saving', 'Saving...')}
                  </span>
                ) : (
                  t('feeds.reviewFeed.save', 'Save & Sync Reviews')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FeedManager
